import React, { useMemo } from 'react';
import CustomDropdown from './CustomDropdown';
import CustomNumericInput from './CustomNumericInput';
import { Box, Typography } from '@mui/material';
import { getMaxGenerateDaysBefore } from '../utils/frequencyCalculations';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_NAMES_PLURAL = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];
const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Component that displays frequency configuration in sentence format
 * @param {Object} props
 * @param {string} props.frequency - Frequency type
 * @param {Object} props.config - Frequency configuration object
 * @param {Function} props.onChange - Callback when config changes (config) => void
 * @param {boolean} props.readOnly - If true, display as read-only text
 * @param {boolean} props.showAdvancedOptions - If true, show generate/reminder days
 * @param {boolean} props.showReminderDays - If false, hide reminder-days (generate-ahead still shows)
 * @param {boolean} props.menuPortal - If true, dropdown menus render in document.body (use inside dialogs)
 */
export default function FrequencySentenceDisplay({ 
  frequency, 
  config = {}, 
  onChange, 
  readOnly = false,
  showAdvancedOptions = true,
  showReminderDays = true,
  menuPortal = false
}) {
  const radioName = useMemo(() => `dayType-${Math.random().toString(36).substr(2, 9)}`, []);
  
  const handleChange = (field, value) => {
    if (onChange) {
      if (field === null) {
        onChange(value);
      } else {
        onChange({ ...config, [field]: value });
      }
    }
  };

  const renderSentence = () => {
    if (!frequency) return null;

    switch (frequency) {
      case 'Daily':
        return <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>Every day</span>;

      case 'Weekly':
        if (readOnly) {
          const dayName = config.FrequencyDayOfWeek !== null && config.FrequencyDayOfWeek !== undefined
            ? DAY_NAMES[config.FrequencyDayOfWeek] || 'day'
            : 'day';
          return <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>Due every {dayName}</span>;
        }
        return (
          <span style={{ color: 'var(--color-text)', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            Due every{' '}
            <CustomDropdown
              options={DAY_NAMES.map((name, idx) => ({ value: idx, label: name }))}
              value={config.FrequencyDayOfWeek}
              onChange={(v) => handleChange('FrequencyDayOfWeek', v)}
              placeholder="Select day"
              width="130px"
              height="40px"
              menuPortal={menuPortal}
            />
          </span>
        );

      case 'Bi-Weekly':
        if (readOnly) {
          const dayName = config.FrequencyDayOfWeek !== null && config.FrequencyDayOfWeek !== undefined
            ? DAY_NAMES_PLURAL[config.FrequencyDayOfWeek] || 'days'
            : 'days';
          const weekType = config.FrequencyWeekType || 'Odd';
          return <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>Due on {dayName} on {weekType} weeks</span>;
        }
        return (
          <span style={{ color: 'var(--color-text)', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            Due on{' '}
            <CustomDropdown
              options={DAY_NAMES_PLURAL.map((name, idx) => ({ value: idx, label: name }))}
              value={config.FrequencyDayOfWeek}
              onChange={(v) => handleChange('FrequencyDayOfWeek', v)}
              placeholder="Select day"
              width="80px"
              height="40px"
              menuPortal={menuPortal}
            />
            {' '}on{' '}
            <CustomDropdown
              options={[
                { value: 'Odd', label: 'Odd' },
                { value: 'Even', label: 'Even' }
              ]}
              value={config.FrequencyWeekType}
              onChange={(v) => handleChange('FrequencyWeekType', v)}
              placeholder="Select"
              width="80px"
              height="40px"
              menuPortal={menuPortal}
            />
            {' '}weeks
          </span>
        );

      case 'Monthly':
      case 'Bi-Monthly':
      case 'Quarterly':
      case 'Semi-Annually':
      case 'Annually':
      case 'Every x years':
        return renderMonthlyFrequency(frequency, config, readOnly, handleChange, radioName);

      default:
        return null;
    }
  };

  const renderMonthlyFrequency = (freq, cfg, readOnly, handleChange, radioName) => {
    const dayType = cfg.FrequencyDayType;
    const isDayNumber = dayType === 'DayNumber';
    const isLastDay = dayType === 'LastDay';
    const isWeekday = ['FirstWeekday', 'SecondWeekday', 'ThirdWeekday', 'LastWeekday'].includes(dayType);

    if (readOnly) {
      let dayText = '';
      if (isDayNumber && cfg.FrequencyDayNumber) {
        dayText = cfg.FrequencyDayNumber > 28 ? 'last day' : `${cfg.FrequencyDayNumber}`;
      } else if (isLastDay) {
        dayText = 'last day';
      } else if (isWeekday) {
        const weekdayLabels = { FirstWeekday: 'First', SecondWeekday: 'Second', ThirdWeekday: 'Third', LastWeekday: 'Last' };
        const dayName = cfg.FrequencyWeekday !== null && cfg.FrequencyWeekday !== undefined
          ? DAY_NAMES[cfg.FrequencyWeekday] || 'day'
          : 'day';
        dayText = `${weekdayLabels[dayType]} ${dayName}`;
      }

      let suffix = '';
      if (freq === 'Bi-Monthly') {
        suffix = ` on ${cfg.FrequencyWeekType || 'Odd'} months`;
      } else if (freq === 'Quarterly') {
        suffix = ` in the ${cfg.FrequencyMonthType || 'First'} month of the quarter`;
      } else if (freq === 'Semi-Annually') {
        const month1 = MONTH_NAMES[cfg.FrequencyMonth] || '';
        const month2 = cfg.FrequencyMonth ? MONTH_NAMES[(cfg.FrequencyMonth + 6 - 1) % 12 + 1] : '';
        suffix = ` in ${month1} and ${month2}`;
      } else if (freq === 'Annually') {
        suffix = ` in ${MONTH_NAMES[cfg.FrequencyMonth] || ''}`;
      } else if (freq === 'Every x years') {
        suffix = ` in ${MONTH_NAMES[cfg.FrequencyMonth] || ''} every ${cfg.FrequencyYears || ''} years`;
      } else {
        suffix = ' of the month';
      }

      return <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>Due on the {dayText}{suffix}</span>;
    }

    // Editable mode
    const isDayNumberMode = !dayType || dayType === 'DayNumber' || dayType === 'LastDay';
    const isWeekdayMode = ['FirstWeekday', 'SecondWeekday', 'ThirdWeekday', 'LastWeekday'].includes(dayType);
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Radio buttons section with BLACK background */}
        <div style={{ 
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid var(--color-dropdown-border)',
          backgroundColor: 'var(--color-bg-input)',
          display: 'flex', 
          gap: '24px', 
          alignItems: 'center',
          minHeight: '56px'
        }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            cursor: 'pointer', 
            color: 'var(--color-text)', 
            fontSize: '15px', 
            userSelect: 'none',
          }}>
            <input
              type="radio"
              name={radioName}
              checked={isDayNumberMode}
              onChange={() => {
                const newConfig = { ...cfg };
                newConfig.FrequencyDayType = 'DayNumber';
                newConfig.FrequencyDayNumber = cfg.FrequencyDayNumber || 1;
                newConfig.FrequencyWeekday = null;
                handleChange(null, newConfig);
              }}
              style={{
                margin: 0,
                cursor: 'pointer',
                accentColor: 'var(--color-accent)',
                width: '18px',
                height: '18px',
              }}
            />
            <span>Specific day of month</span>
          </label>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            cursor: 'pointer', 
            color: 'var(--color-text)', 
            fontSize: '15px', 
            userSelect: 'none',
          }}>
            <input
              type="radio"
              name={radioName}
              checked={isWeekdayMode}
              onChange={() => {
                const newConfig = { ...cfg };
                newConfig.FrequencyDayType = cfg.FrequencyDayType && ['FirstWeekday', 'SecondWeekday', 'ThirdWeekday', 'LastWeekday'].includes(cfg.FrequencyDayType) 
                  ? cfg.FrequencyDayType 
                  : 'FirstWeekday';
                newConfig.FrequencyWeekday = cfg.FrequencyWeekday !== null && cfg.FrequencyWeekday !== undefined ? cfg.FrequencyWeekday : 1;
                newConfig.FrequencyDayNumber = null;
                handleChange(null, newConfig);
              }}
              style={{
                margin: 0,
                cursor: 'pointer',
                accentColor: 'var(--color-accent)',
                width: '18px',
                height: '18px',
              }}
            />
            <span>Specific weekday occurrence</span>
          </label>
        </div>

        {/* Day number mode section with BLACK background */}
        {isDayNumberMode && (
          <div style={{
            marginTop: '8px',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid var(--color-dropdown-border)',
            backgroundColor: 'var(--color-bg-input)',
          }}>
            <Typography variant="body2" sx={{ color: 'var(--color-text)', fontSize: '16px', mb: 1 }}>
              Due on the
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {dayType === 'LastDay' ? (
                <>
                  <span style={{ color: 'var(--color-text)', fontSize: '16px', fontWeight: 'bold' }}>last day</span>
                  <button
                    type="button"
                    onClick={() => {
                      handleChange(null, {
                        ...cfg,
                        FrequencyDayType: 'DayNumber',
                        FrequencyDayNumber: cfg.FrequencyDayNumber || 15
                      });
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--color-accent)',
                      cursor: 'pointer',
                      fontSize: '14px',
                      textDecoration: 'underline',
                      padding: 0
                    }}
                  >
                    use specific day
                  </button>
                </>
              ) : (
                <>
                  <CustomNumericInput
                    value={cfg.FrequencyDayNumber || ''}
                    onChange={(v) => {
                      const num = parseInt(v, 10) || 0;
                      handleChange(null, {
                        ...cfg,
                        FrequencyDayType: 'DayNumber',
                        FrequencyDayNumber: num
                      });
                    }}
                    min={1}
                    max={31}
                    width="100px"
                    height="40px"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      handleChange(null, {
                        ...cfg,
                        FrequencyDayType: 'LastDay',
                        FrequencyDayNumber: null
                      });
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--color-accent)',
                      cursor: 'pointer',
                      fontSize: '14px',
                      textDecoration: 'underline',
                      padding: 0
                    }}
                  >
                    use last day
                  </button>
                </>
              )}
              <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>
                {dayType === 'LastDay' ? '' : 'of the month'}
              </span>
              {freq === 'Bi-Monthly' && (
                <>
                  <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>on</span>
                  <CustomDropdown
                    options={[
                      { value: 'Odd', label: 'Odd' },
                      { value: 'Even', label: 'Even' }
                    ]}
                    value={cfg.FrequencyWeekType}
                    onChange={(v) => handleChange('FrequencyWeekType', v)}
                    width="90px"
                    height="40px"
                    menuPortal={menuPortal}
                  />
                  <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>months</span>
                </>
              )}
              {freq === 'Quarterly' && (
                <>
                  <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>in the</span>
                  <CustomDropdown
                    options={[
                      { value: 'First', label: 'First' },
                      { value: 'Second', label: 'Second' },
                      { value: 'Third', label: 'Third' }
                    ]}
                    value={cfg.FrequencyMonthType}
                    onChange={(v) => handleChange('FrequencyMonthType', v)}
                    width="110px"
                    height="40px"
                    menuPortal={menuPortal}
                  />
                  <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>month of the quarter</span>
                </>
              )}
              {(freq === 'Semi-Annually' || freq === 'Annually' || freq === 'Every x years') && (
              <>
                <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>in</span>
                <CustomDropdown
                  options={MONTH_NAMES.slice(1).map((name, idx) => ({ value: idx + 1, label: name }))}
                  value={cfg.FrequencyMonth}
                  onChange={(v) => handleChange('FrequencyMonth', v)}
                  width="130px"
                  height="40px"
                  menuPortal={menuPortal}
                />
                {freq === 'Semi-Annually' && cfg.FrequencyMonth && (
                  <>
                    <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>and</span>
                    <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>
                      {MONTH_NAMES[(cfg.FrequencyMonth + 6 - 1) % 12 + 1]}
                    </span>
                  </>
                )}
                {freq === 'Every x years' && (
                  <>
                    <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>every</span>
                    <CustomNumericInput
                      value={cfg.FrequencyYears || ''}
                      onChange={(v) => handleChange('FrequencyYears', v)}
                      min={1}
                      max={100}
                      width="100px"
                      height="40px"
                    />
                    <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>years</span>
                  </>
                )}
              </>
            )}
            </Box>
          </div>
        )}

        {/* Weekday mode section with BLACK background */}
        {isWeekdayMode && (
          <div style={{
            marginTop: '8px',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid var(--color-dropdown-border)',
            backgroundColor: 'var(--color-bg-input)',
          }}>
            <Typography variant="body2" sx={{ color: 'var(--color-text)', fontSize: '16px', mb: 1 }}>
              Due on the
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <CustomDropdown
                options={[
                  { value: 'FirstWeekday', label: 'First' },
                  { value: 'SecondWeekday', label: 'Second' },
                  { value: 'ThirdWeekday', label: 'Third' },
                  { value: 'LastWeekday', label: 'Last' }
                ]}
                value={dayType}
                onChange={(v) => handleChange('FrequencyDayType', v)}
                width="110px"
                height="40px"
                menuPortal={menuPortal}
              />
              <CustomDropdown
                options={DAY_NAMES.map((name, idx) => ({ value: idx, label: name }))}
                value={cfg.FrequencyWeekday}
                onChange={(v) => handleChange('FrequencyWeekday', v)}
                placeholder="Select day"
                width="130px"
                height="40px"
                menuPortal={menuPortal}
              />
              {freq === 'Monthly' && (
                <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>
                  of the month
                </span>
              )}
              {freq === 'Bi-Monthly' && (
                <>
                  <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>on</span>
                  <CustomDropdown
                    options={[
                      { value: 'Odd', label: 'Odd' },
                      { value: 'Even', label: 'Even' }
                    ]}
                    value={cfg.FrequencyWeekType}
                    onChange={(v) => handleChange('FrequencyWeekType', v)}
                    width="90px"
                    height="40px"
                    menuPortal={menuPortal}
                  />
                  <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>months</span>
                </>
              )}
              {freq === 'Quarterly' && (
                <>
                  <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>in the</span>
                  <CustomDropdown
                    options={[
                      { value: 'First', label: 'First' },
                      { value: 'Second', label: 'Second' },
                      { value: 'Third', label: 'Third' }
                    ]}
                    value={cfg.FrequencyMonthType}
                    onChange={(v) => handleChange('FrequencyMonthType', v)}
                    width="110px"
                    height="40px"
                    menuPortal={menuPortal}
                  />
                  <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>month of the quarter</span>
                </>
              )}
              {(freq === 'Semi-Annually' || freq === 'Annually' || freq === 'Every x years') && (
                <>
                  <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>in</span>
                  <CustomDropdown
                    options={MONTH_NAMES.slice(1).map((name, idx) => ({ value: idx + 1, label: name }))}
                    value={cfg.FrequencyMonth}
                    onChange={(v) => handleChange('FrequencyMonth', v)}
                    width="130px"
                    height="40px"
                    menuPortal={menuPortal}
                  />
                  {freq === 'Semi-Annually' && cfg.FrequencyMonth && (
                    <>
                      <span style={{ color: '#fff', fontSize: '16px' }}>and</span>
                      <span style={{ color: '#fff', fontSize: '16px' }}>
                        {MONTH_NAMES[(cfg.FrequencyMonth + 6 - 1) % 12 + 1]}
                      </span>
                    </>
                  )}
                  {freq === 'Every x years' && (
                    <>
                      <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>every</span>
                      <CustomNumericInput
                        value={cfg.FrequencyYears || ''}
                        onChange={(v) => handleChange('FrequencyYears', v)}
                        min={1}
                        max={100}
                        width="100px"
                        height="40px"
                      />
                      <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>years</span>
                    </>
                  )}
                </>
              )}
            </Box>
          </div>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ py: 1 }}>
      {renderSentence()}
      {showAdvancedOptions && !readOnly && (
        <div style={{
          marginTop: '24px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          borderRadius: '8px',
          border: '1px solid var(--color-dropdown-border)',
          backgroundColor: 'var(--color-bg-input)',
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>Generate</span>
              <CustomNumericInput
                value={config.GenerateDaysBefore || ''}
                onChange={(v) => {
                  const maxDays = getMaxGenerateDaysBefore(frequency, config.FrequencyYears);
                  const numValue = v === '' || v === null ? 0 : Number(v);
                  if (numValue > maxDays) {
                    handleChange('GenerateDaysBefore', maxDays);
                  } else {
                    handleChange('GenerateDaysBefore', v);
                  }
                }}
                min={0}
                max={frequency ? getMaxGenerateDaysBefore(frequency, config.FrequencyYears) : undefined}
                width="100px"
                height="40px"
              />
              <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>days before due date</span>
            </Box>
            {frequency && (config.GenerateDaysBefore || 0) > 0 && (
              <div style={{ color: 'var(--color-text-secondary)', fontSize: '12px', fontStyle: 'italic', marginLeft: '100px' }}>
                Maximum for {frequency}: {getMaxGenerateDaysBefore(frequency, config.FrequencyYears)} days
              </div>
            )}
          </Box>
          {/* Only show ReminderDays if GenerateDaysBefore is greater than 1 */}
          {showReminderDays && (config.GenerateDaysBefore || 0) > 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>Send reminder</span>
                <CustomNumericInput
                  value={config.ReminderDays || ''}
                  onChange={(v) => {
                    const generateDays = config.GenerateDaysBefore || 0;
                    const reminderDays = v === '' || v === null ? 0 : Number(v);
                    // ReminderDays must be at least 1 day less than GenerateDaysBefore
                    const maxReminderDays = generateDays > 0 ? generateDays - 1 : undefined;
                    if (maxReminderDays !== undefined && reminderDays > maxReminderDays) {
                      handleChange('ReminderDays', maxReminderDays);
                    } else {
                      handleChange('ReminderDays', v);
                    }
                  }}
                  min={0}
                  max={config.GenerateDaysBefore > 0 ? config.GenerateDaysBefore - 1 : undefined}
                  width="100px"
                  height="40px"
                />
                <span style={{ color: 'var(--color-text)', fontSize: '16px' }}>days before due date</span>
              </Box>
              {config.GenerateDaysBefore > 0 && (config.ReminderDays || 0) > 0 && (
                <div style={{ color: 'var(--color-text-secondary)', fontSize: '12px', fontStyle: 'italic', marginLeft: '100px' }}>
                  Must be less than Generate Days Before ({config.GenerateDaysBefore} days). Maximum: {config.GenerateDaysBefore - 1} days
                </div>
              )}
              {config.GenerateDaysBefore > 0 && (config.ReminderDays || 0) >= config.GenerateDaysBefore && (
                <div style={{ color: 'var(--color-error)', fontSize: '12px', fontStyle: 'italic', marginLeft: '100px' }}>
                  Reminder Days must be at least 1 day less than Generate Days Before
                </div>
              )}
            </Box>
          )}
        </div>
      )}
      {showAdvancedOptions && readOnly && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {config.GenerateDaysBefore > 0 && (
            <span style={{ color: 'var(--color-text)', fontSize: '14px' }}>
              Generate {config.GenerateDaysBefore} days before due date
            </span>
          )}
          {showReminderDays && config.ReminderDays > 0 && (
            <span style={{ color: 'var(--color-text)', fontSize: '14px' }}>
              Send reminder {config.ReminderDays} days before due date
            </span>
          )}
        </Box>
      )}
    </Box>
  );
}
